/**
 * Integer-atom inventory math.
 *
 * "Atoms" are the smallest base unit on a product family — every quantity
 * is expressed as an integer multiple of an atom. For a product whose
 * parent unit holds 336 pieces, ATOM_FACTOR = 336 and one piece = 1 atom.
 *
 * All arithmetic runs in integer atoms; we divide back to the consolidated
 * stock value exactly once, at the boundary. This eliminates the
 * floating-point drift inherent in `* fraction` math and removes the
 * possibility of `fraction` and `qty_in_package` getting out of sync on a
 * given product giving two different breakdowns of the same stock value
 * across screens (the classic "InventoryBreakdown popover says 10/4/24,
 * QuickCount says 10/13/28" bug).
 *
 * Domain invariant (enforced by these helpers):
 *   child.fraction === child.quantity_in_unit_package / parent.quantity_in_unit_package
 * If callers pass products where this invariant is violated, the atom
 * helpers ignore `fraction` and use `quantity_in_unit_package` as the
 * single source of truth — same value used by every screen.
 */

import { BreakdownItem } from '../types/shared.types';
import {
  StoreProduct,
  StoreProductFraction,
  Product,
} from '../types/products.types';

/** Subset of fields we need from a product or fraction to do atom math. */
export interface AtomicFraction {
  id: string;
  unit: string | number;
  /** Atoms-per-unit for this row (parent or child). Integer. */
  quantity_in_unit_package: number;
  /** Decimal fraction relative to parent. Retained for breakdown output only. */
  fraction: number;
}

/** Row used by `decomposeAtoms` and `aggregateAtoms`. */
export interface AtomRow {
  id: string;
  unit: string | number;
  fraction: number;
  /** Integer atoms in one unit of this row. */
  qty_in_package: number;
}

const cleanUnitName = (unit: string | number): string =>
  String(unit ?? '').replace(/[()]/g, '');

/** Integer atom count per parent unit. Always `>= 1`. */
export const atomFactorOf = (parent: {
  quantity_in_unit_package?: number | null;
}): number => Math.max(1, Math.round(parent?.quantity_in_unit_package ?? 1));

/**
 * Atoms-per-unit for a row (parent or child). Always `>= 1`. Children store
 * their own `quantity_in_unit_package`; we use it directly.
 */
export const qtyInPackageOf = (row: {
  quantity_in_unit_package?: number | null;
}): number => Math.max(1, Math.round(row?.quantity_in_unit_package ?? 1));

/**
 * Convert a consolidated stock value to integer atoms. The single
 * floating-point → integer boundary in the system.
 */
export const atomsFromStock = (
  stock: number,
  parent: { quantity_in_unit_package?: number | null },
): number => Math.round((stock || 0) * atomFactorOf(parent));

/** Convert integer atoms back to the consolidated stock value. */
export const stockFromAtoms = (
  atoms: number,
  parent: { quantity_in_unit_package?: number | null },
): number => atoms / atomFactorOf(parent);

/**
 * Sum a list of `(quantity, qty_in_package)` rows into integer atoms.
 * `quantity` is the user-entered count for that row (whole packs for
 * non-weight rows; may be fractional for weight rows, in which case
 * `Math.round` absorbs the single boundary conversion).
 */
export const aggregateAtoms = (
  rows: Array<{ quantity: number; qty_in_package: number }>,
): number =>
  rows.reduce(
    (sum, r) => sum + Math.round((r.quantity || 0) * (r.qty_in_package || 1)),
    0,
  );

export interface DecomposeOptions {
  /**
   * When true, supports negative atom totals and preserves sign on each row.
   * Greedy decomposition still cannot mix signs within one call (a single
   * signed total maps to same-signed rows); callers that need mixed-sign
   * output (e.g. per-row deltas) should diff per-row themselves.
   */
  signed?: boolean;
  /**
   * Drop rows whose `units === 0` from the returned array. Defaults to
   * true (matches the legacy `calculateInventoryBreakdown` behavior).
   */
  dropZeros?: boolean;
}

/**
 * Greedy largest-pack-first decomposition of an atom total into integer
 * unit counts per row. `rows` are normalized internally — caller does not
 * need to sort.
 */
export const decomposeAtoms = (
  atoms: number,
  rows: AtomRow[],
  options: DecomposeOptions = {},
): BreakdownItem[] => {
  const { signed = false, dropZeros = true } = options;

  if (!rows || rows.length === 0) return [];
  if (!signed && atoms <= 0) return [];

  const sign = atoms < 0 ? -1 : 1;
  let remaining = Math.abs(Math.round(atoms));

  // Largest pack first; integer remainders only.
  const sorted = [...rows].sort((a, b) => b.qty_in_package - a.qty_in_package);

  const out: BreakdownItem[] = [];
  for (const r of sorted) {
    if (!r.qty_in_package || r.qty_in_package <= 0) continue;
    const units = Math.floor(remaining / r.qty_in_package);
    remaining -= units * r.qty_in_package;
    const signedUnits = sign * units;
    if (dropZeros && signedUnits === 0) continue;
    out.push({
      id: r.id,
      name: cleanUnitName(r.unit) || `Box=${r.qty_in_package}`,
      fraction: r.fraction,
      units: signedUnits,
    });
  }
  return out;
};

/**
 * Build the canonical `AtomRow[]` for a product: `[parent, ...children]`
 * with stable units and `qty_in_package` derived from each row's own
 * `quantity_in_unit_package`. Children with `fraction >= 1` (i.e. the
 * parent re-listed as a fraction) are de-duplicated by id.
 */
export const buildFractionRows = (
  product: StoreProduct | Product,
  fractions?: ReadonlyArray<StoreProductFraction | Product>,
  parentUnit?: string | number,
): AtomRow[] => {
  const rows: AtomRow[] = [];
  const seen = new Set<string>();

  const parentQty = qtyInPackageOf(product);
  rows.push({
    id: product.id,
    unit: parentUnit ?? product.unit ?? `Box=${parentQty}`,
    fraction: 1,
    qty_in_package: parentQty,
  });
  seen.add(product.id);

  const list =
    fractions ??
    ((product as StoreProduct).store_product_fractions as
      | ReadonlyArray<StoreProductFraction>
      | undefined) ??
    [];

  for (const f of list) {
    if (!f || seen.has(f.id)) continue;
    if (f.id === product.id) continue;
    // Skip rows that don't have a smaller pack than the parent.
    const childQty = qtyInPackageOf(f);
    rows.push({
      id: f.id,
      unit:
        f.unit ?? (f as ProductFractionLike).fraction_name ?? `Box=${childQty}`,
      fraction: typeof f.fraction === 'number' ? f.fraction : 1,
      qty_in_package: childQty,
    });
    seen.add(f.id);
  }

  return rows;
};

/** Local helper type to avoid importing the full `ProductFraction` type. */
interface ProductFractionLike {
  fraction_name?: string;
}

/**
 * Build atom rows from a flat fractions array that may or may not contain
 * a `fraction === 1` parent entry. Mirrors the legacy
 * `calculateInventoryBreakdown` input shape so we can swap internals
 * without changing call sites.
 */
export const buildFractionRowsFromList = (
  fractions: ReadonlyArray<AtomicFraction>,
  parentUnit?: string | number,
): AtomRow[] => {
  if (!fractions || fractions.length === 0) return [];

  const list = [...fractions];
  const parent = list.find((f) => f.fraction === 1) ?? list[0];
  const others = list.filter((f) => f.id !== parent.id);

  const rows: AtomRow[] = [];
  const parentQty = qtyInPackageOf(parent);
  rows.push({
    id: parent.id,
    unit: parentUnit ?? parent.unit ?? `Box=${parentQty}`,
    fraction: 1,
    qty_in_package: parentQty,
  });

  const seen = new Set<string>([parent.id]);
  for (const f of others) {
    if (seen.has(f.id)) continue;
    const childQty = qtyInPackageOf(f);
    rows.push({
      id: f.id,
      unit: f.unit ?? `Box=${childQty}`,
      fraction: typeof f.fraction === 'number' ? f.fraction : 1,
      qty_in_package: childQty,
    });
    seen.add(f.id);
  }

  return rows;
};
